import { API_PROVIDERS } from './modelConstants';

export const emptyConfig = (configIndex = 1, configName = '') => ({
  config_index: configIndex,
  config_name: configName,
  api_key: '',
  endpoint: '',
  access_key: '',
  secret_key: '',
  region: '',
});

export const defaultProviderConfigs = () =>
  Object.fromEntries(API_PROVIDERS.map(p => [p, [emptyConfig(1)]]));

export const isMasked = (val) => val && /^\*+/.test(val);
export const CREDENTIAL_FIELDS = ['api_key', 'endpoint', 'access_key', 'secret_key', 'region'];
export const TRIMMED_CREDENTIAL_FIELDS = new Set(['endpoint', 'region']);
// Shown in full: a region isn't a secret, and masked it can't be checked.
const UNMASKED_CREDENTIAL_FIELDS = new Set(['region']);
export const MAX_PERSONAS = 5;

// How a config reads once saved, as GET /api/user/api_keys returns it.
export const maskSavedConfig = (cfg) => {
  const masked = { ...cfg };
  CREDENTIAL_FIELDS.forEach((field) => {
    if (UNMASKED_CREDENTIAL_FIELDS.has(field)) return;
    if (masked[field] && !isMasked(masked[field])) {
      masked[field] = `******${masked[field].slice(-4)}`;
    }
  });
  return masked;
};

export const emptyPersona = (sortOrder) => ({ id: '', sort_order: sortOrder, title: '', prompt: '' });

export const normalizePersonasForState = (incoming = []) => {
  const rows = Array.isArray(incoming)
    ? incoming.map((p, idx) => ({
      id: p?.id || '',
      sort_order: p?.sort_order ?? (idx + 1),
      title: p?.title || '',
      prompt: p?.prompt || '',
    })).slice(0, MAX_PERSONAS)
    : [];

  while (rows.length < MAX_PERSONAS) {
    rows.push(emptyPersona(rows.length + 1));
  }
  return rows;
};

export const DEFAULT_AGENT_ASSIGNMENT = {
  provider: 'stackform',
  model_name: 'default-rapid-model',
  persona_id: '',
};

// The three built-in default sub-agents.
export const DEFAULT_AGENT_NAMES = ['aeri', 'capo', 'cardian'];

// Sentinel provider value marking an agent slot as disabled. A disabled
// agent is dropped from every mode; at least one must stay enabled (Swarm
// needs >= 2). Mirrors the backend ``model_utils_helpers.DISABLED_PROVIDER``;
// keep the two in sync.
export const DISABLED_PROVIDER = 'none';

export const isAgentDisabled = (assignment) =>
  String(assignment?.provider || '').trim().toLowerCase() === DISABLED_PROVIDER;

const hasText = (cfg, field) => Boolean(String(cfg[field] || '').trim());

// Whether a saved provider config can serve a model: an endpoint for the
// local provider (Ollama, or Connect's connect://<token>), for AWS a
// region with an API key or an access key and secret key, else a key. Mirrors
// the backend run-start check (``node_models._unavailable_reason``); keep the
// two in sync.
export const isUsableProviderConfig = (provider, cfg) => {
  if (!cfg) return false;
  if (provider === 'local') return hasText(cfg, 'endpoint');
  if (provider === 'aws') {
    return hasText(cfg, 'region') && (
      hasText(cfg, 'api_key')
      || (hasText(cfg, 'access_key') && hasText(cfg, 'secret_key'))
    );
  }
  return Boolean(cfg.api_key);
};

export const normalizeFieldValue = (field, value) => {
  if (typeof value !== 'string') return value;
  if (TRIMMED_CREDENTIAL_FIELDS.has(field)) {
    return value.trim();
  }
  return value;
};

// A main model's per-call settings, all unset.
export const EMPTY_MODEL_SETTINGS = {
  maxTokens: '', temperature: '', topP: '', timeout: '',
  presencePenalty: '', frequencyPenalty: '',
};

// The main model a profile goes back to when its key is deleted: the
// platform default a new user starts with. Mirrors the backend
// ``deleted_keys.PLATFORM_MAIN_MODEL``; keep the two in sync.
export const PLATFORM_MAIN_MODEL = { provider: 'default', model: 'default-model' };

// The sub-agents a key delete reset (default agents) or cleared (custom
// ones) in one profile, by name. The reply lists each with its profile;
// an entry without one is left out.
export const agentsResetIn = (reply = {}, profileName) => {
  const inProfile = (entries) => (entries || [])
    .filter((entry) => entry && entry.profile === profileName)
    .map((entry) => entry.agent);
  return {
    reset: inProfile(reply.default_agents_reset),
    cleared: inProfile(reply.custom_agents_cleared),
  };
};

const countOf = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// What deleting a saved key reset, from the DELETE /api/user/provider_config
// reply, as one sentence; null when no choice used the key.
export const describeKeyReset = (reply = {}) => {
  const agents = (reply.default_agents_reset || []).length
    + (reply.custom_agents_cleared || []).length;
  const mains = (reply.main_models_reset || []).length;
  const summarizers = (reply.summarizer_models_cleared || []).length;
  const workflows = reply.workflow_nodes_reset || [];
  const nodes = workflows.reduce((n, w) => n + (w.nodes || []).length, 0);
  const parts = [
    agents && countOf(agents, 'sub-agent'),
    mains && `the main model in ${countOf(mains, 'profile')}`,
    summarizers && `the Summarizer Model in ${countOf(summarizers, 'profile')}`,
    nodes && `${countOf(nodes, 'workflow node')} in ${countOf(workflows.length, 'workflow')}`,
  ].filter(Boolean);
  if (!parts.length) return null;
  const list = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `Key deleted. Reset what used it: ${list}.`;
};
